import * as ko from "knockout";
import { system, Router } from "portalframework";
import { WindowFormat } from "portalframework";
import { userService } from "../user-service";
import * as toastr from "toastr";
import { TfaSettings } from "organized";
import labels from "app/labels";

export interface RouteDTO {
  url: string;
  component_name: string;
  description: string;
  roles: string;
}

export interface LastKnownPath {
  path: string;
  personId: any;
  created: string;
}

export class RoutesService {
  public static LAST_KNOWN_PATH = "LAST_KNOWN_PATH_BEFORE_SUDDEN_LOG_OUT";

  private _router: Router;
  public static Routes: RouteDTO[] = [];
  public static GuardIsActivated: boolean = false;

  public static TwoFactorSettings: KnockoutObservable<TfaSettings> = ko.observable(new TfaSettings());
  get currentRoute() {
    return this._router.currentRoute;
  }

  initializeRouter = (history: unknown, routesMapped: any) => {
    this._router = new Router(system.history, routesMapped);
  };

  setInitialNavigation = async (givenUrl: string = null) => {
    const hasNavigatedToLastKnownPath = this.navigateToLastKnownPath();

    if (hasNavigatedToLastKnownPath) {
      return;
    }

    if (givenUrl) {
      this.navigate(givenUrl);
      return;
    }

    this.navigate("/");
  };

  navigateToComponent = (component_name: string, url: string, id: string = null) => {
    const check = this.checkRestrictedUser();
    if (check) {
      // dont use private method to avoid circular dependency
      system.navigate("/my_account");
      return;
    }

    let routeFound: RouteDTO = RoutesService.Routes.find((r) => r.component_name === component_name);
    if (RoutesService.GuardIsActivated) {
      const routeRoles = routeFound?.roles?.split(";")?.filter((r) => r) || [];
      if (!routeFound || !this.containsAtLeastOneRole(userService.CurrentUser.roles, routeRoles)) {
        toastr.warning("cc_P_NO_PAGE_ACCESS");
      } else {
        this.openUrl(url, id);
      }
    } else {
      this.openUrl(url, id);
    }
  };

  openInNewTab = (url: string, id: string = null) => {
    const generateUrl = this.generateUrl(url, id);
    if (RoutesService.GuardIsActivated) {
      let routeFound = this.getRoute(url, id);
      if (routeFound && this.isPermittedRoute(routeFound)) {
        window.open(generateUrl, "_blank");
      } else {
        toastr.warning("cc_P_NO_PAGE_ACCESS");
      }
    } else {
      window.open(generateUrl, "_blank");
    }
  };

  navigate = (url: string, id: string = null, redirectToHomeIfError = false) => {
    const check = this.checkRestrictedUser();
    if (check) {
      // dont use private method to avoid circular dependency
      system.navigate("/my_account");
      return;
    }
    if (RoutesService.GuardIsActivated) {
      let routeFound = this.getRoute(url, id);

      if (routeFound && this.isPermittedRoute(routeFound)) {
        this.openUrl(url, id);
      } else {
        toastr.warning("cc_P_NO_PAGE_ACCESS");
        if (redirectToHomeIfError) {
          // dont use private method to avoid circular dependency
          system.navigate("/");
        }
      }
    } else {
      this.openUrl(url, id);
    }
  };

  private getRoute = (url: string, id: string = null): RouteDTO => {
    let rootUrl = url;
    let routeFound: RouteDTO;
    if (!id) {
      const cleandUrl = rootUrl.startsWith("/") ? rootUrl.substring(1) : rootUrl;
      routeFound = RoutesService.Routes.find((r) => r.url === cleandUrl);
    } else {
      let cleandUrl = rootUrl.startsWith("/") ? rootUrl.substring(1) : rootUrl; // need not to start with '/'
      cleandUrl = cleandUrl.endsWith("/") ? cleandUrl : `${cleandUrl}/`; // needs to end with '/'
      routeFound = RoutesService.Routes.find((r) => r.url.includes(cleandUrl));
    }

    return routeFound;
  };

  private isPermittedRoute = (route: RouteDTO): boolean => {
    const routeRoles = route?.roles?.split(";")?.filter((r) => r) || [];
    return this.containsAtLeastOneRole(userService.CurrentUser.roles, routeRoles);
  };

  containsAtLeastOneRole = (userRoles: string[], pageRoles: string[]) => {
    if (pageRoles.length === 0) {
      return true;
    }

    for (let i = 0; i < pageRoles.length; i++) {
      if (userRoles.includes(pageRoles[i])) {
        return true;
      }
    }

    return false;
  };

  private generateUrl = (url: string, id: string) => {
    if (!id) {
      return url;
    } else {
      const finalUrl = `${url}/${id}`;
      return finalUrl;
    }
  };

  private openUrl = (url: string, id: string) => {
    const urlGenerated = this.generateUrl(url, id);
    if (window.location.pathname === urlGenerated) {
      window.location.reload();
    } else {
      system.navigate(urlGenerated);
    }
  };

  private checkRestrictedUser = () => {
    if (RoutesService.TwoFactorSettings().restrictUser()) {
      toastr.error(labels.descr("cc_P_RESTRICTED_USER"));
      return true;
    }
    return false;
  };
  openGoogleMapsWithCoords = (lat: string, lng: string) => {
    const deepLink = `https://maps.google.com/?q=${lat},${lng}`;
    window.location.href = deepLink;
  };

  openGoogleMapsWithAddress = (address: string) => {
    const encodedAddress = encodeURIComponent(address);

    const deepLink = `https://maps.google.com/?q=${address}`;
    window.location.href = deepLink;
  };

  private navigateToLastKnownPath = () => {
    const last_known_path_data = localStorage.getItem(RoutesService.LAST_KNOWN_PATH);

    let data: LastKnownPath | null = null;

    try {
      data = JSON.parse(last_known_path_data);
    } catch {
      localStorage.removeItem(RoutesService.LAST_KNOWN_PATH);
    }

    if (data && data.created && data.path && data.personId) {
      const now = new Date();
      const thirtyMinutesAgo = new Date(now.getTime() - 30 * 60 * 1000);

      const parsedDate = new Date(data.created);
      const isInLast30Minutes = parsedDate > thirtyMinutesAgo && parsedDate <= now;

      const samePersonId = data.personId === userService.CurrentUser.personId;

      if (isInLast30Minutes && samePersonId) {
        localStorage.removeItem(RoutesService.LAST_KNOWN_PATH);
        if (data.path && data.path !== "null" && !data.path.includes("login") && !data.path.includes("logout")) {
          let path = data.path;
          const url_id = path.split("/").pop();

          if (isNaN(parseInt(url_id))) {
            this.navigate(path, null, true);
          } else {
            const id = parseInt(url_id);
            path = path.replace(url_id, "");
            this.navigate(path, `${id}`, true);
          }
          return true;
        }
      } else {
        localStorage.removeItem(RoutesService.LAST_KNOWN_PATH);
      }

      return false;
    }
  };
}

export const routesService = new RoutesService();
